import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Gift, Star, TrendingUp, TrendingDown, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function CustomerLoyalty() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);

  const fetchData = () => {
    api.get('/customer/loyalty').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const handleClaim = async (reward) => {
    if (!window.confirm(`Tukar ${reward.points_required} poin untuk "${reward.name}"?`)) return;
    setClaiming(reward.id);
    try {
      const res = await api.post('/customer/loyalty/claim-reward', { reward_id: reward.id });
      toast.success(res.data.message);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menukar reward');
    } finally {
      setClaiming(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--store-primary, #2D6CDF)]" /></div>;

  if (!data?.enabled) {
    return (
      <div data-testid="customer-loyalty">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Plus Jakarta Sans' }}>Loyalty & Reward</h1>
        <Card><CardContent className="p-12 text-center text-slate-500">
          <Gift size={48} className="mx-auto mb-4 text-slate-300" />
          <p>Program loyalty belum tersedia di toko ini.</p>
        </CardContent></Card>
      </div>
    );
  }

  const s = data.settings || {};
  const fmt = (n) => new Intl.NumberFormat('id-ID').format(n);

  return (
    <div data-testid="customer-loyalty">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Plus Jakarta Sans' }}>Loyalty & Reward</h1>

      {/* Points Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-5">
            <Star size={20} className="text-amber-500 mb-2" />
            <p className="text-3xl font-bold text-amber-700">{fmt(data.points)}</p>
            <p className="text-xs text-amber-600 mt-1">Poin Saat Ini</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-5">
            <TrendingUp size={20} className="text-emerald-500 mb-2" />
            <p className="text-2xl font-bold">{fmt(data.lifetime_earned)}</p>
            <p className="text-xs text-slate-500 mt-1">Total Poin Didapat</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-5">
            <TrendingDown size={20} className="text-blue-500 mb-2" />
            <p className="text-2xl font-bold">{fmt(data.lifetime_redeemed)}</p>
            <p className="text-xs text-slate-500 mt-1">Total Poin Digunakan</p>
          </CardContent>
        </Card>
      </div>

      {/* How it works */}
      <Card className="mb-6 border-slate-200">
        <CardContent className="p-4">
          <p className="text-sm text-slate-600">
            <strong>Cara dapat poin:</strong> Setiap belanja Rp {fmt(s.points_per_rupiah)} = 1 poin. &nbsp;
            <strong>Cara pakai:</strong> 1 poin = Rp {fmt(s.redemption_value)} diskon saat checkout (min. {s.min_redeem_points} poin, maks. {s.max_redeem_percent}% total belanja).
          </p>
        </CardContent>
      </Card>

      {/* Reward Catalog */}
      {data.rewards?.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-3">Tukar Reward</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.rewards.map(reward => (
              <Card key={reward.id} className="border-slate-200 hover:shadow-md transition-shadow">
                {reward.image_url && <img src={reward.image_url} alt={reward.name} className="w-full h-40 object-cover rounded-t-lg" />}
                <CardContent className="p-4">
                  <h3 className="font-semibold text-slate-900">{reward.name}</h3>
                  {reward.description && <p className="text-sm text-slate-500 mt-1">{reward.description}</p>}
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-amber-600 font-bold text-sm flex items-center gap-1">
                      <Star size={14} /> {fmt(reward.points_required)} poin
                    </span>
                    <span className="text-xs text-slate-400">Stok: {reward.stock}</span>
                  </div>
                  <Button
                    className="w-full mt-3"
                    size="sm"
                    disabled={data.points < reward.points_required || reward.stock <= 0 || claiming === reward.id}
                    onClick={() => handleClaim(reward)}
                  >
                    {claiming === reward.id ? 'Menukar...' : data.points < reward.points_required ? 'Poin Belum Cukup' : 'Tukar Reward'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Transaction History */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader><CardTitle className="text-base">Riwayat Poin</CardTitle></CardHeader>
        <CardContent>
          {data.transactions?.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">Belum ada riwayat poin</p>
          ) : (
            <div className="space-y-2">
              {data.transactions.map(tx => (
                <div key={tx.id} className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0">
                  {tx.points > 0 ? (
                    <ArrowUpCircle size={20} className="text-emerald-500 shrink-0" />
                  ) : (
                    <ArrowDownCircle size={20} className="text-red-500 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">{tx.description}</p>
                    <p className="text-xs text-slate-500">{new Date(tx.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <span className={`text-sm font-bold ${tx.points > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {tx.points > 0 ? '+' : ''}{fmt(tx.points)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
